import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { MultiSelectDropdown } from '../unified/MultiSelectDropdown';
import { siftApiClient } from '../../api/siftApiClient';
import type { AgentAction, AgentScope, AgentSelection } from '../../api/siftApiClient';
import { apiVehicleToSIFTVehicle } from '../../api/dataAdapter';
import type {
  ClientViewDealer,
  ClientViewMapPoint,
  ClientViewDealerCard as ClientViewDealerCardBody,
  InspectorLocation,
  ClientViewVinRow,
  ClientViewCseRepOption,
  CallListClient,
  CallListBand,
  ClientStatusDetails,
  ClientStatusRecord,
} from '../../api/types';
import type { SIFTVehicle } from '../../types/sift';
import { ClientViewDealerCard } from './ClientViewDealerCard';
import { ClientViewMap } from './ClientViewMap';
import { DealerInsightsPopout } from './DealerInsightsPopout';
import { VinDetailsPopout } from './VinDetailsPopout';
import { InspectionCallList } from './InspectionCallList';
import { AgentChatWidget, type AgentBriefing, type AgentHandoff } from '../agent/AgentChatWidget';
import { PriorityChipRow, TierChipRow } from './ChipFilters';
import { CarouselSortControl } from './CarouselSortControl';
import { MetricFiltersPopover } from './MetricFiltersPopover';
import { MetricGrainControl } from './MetricGrainControl';
import { VinStatusFilterPopover } from './VinStatusFilterPopover';
import { ClientSavedViews, type ClientViewAvailable, type ClientViewSelection, type ClientViewSet } from './ClientSavedViews';
import { ClientBoardSearch } from './ClientBoardSearch';
import { useMediaQuery, COMPACT_QUERY, PHONE_QUERY } from '../../hooks/useMediaQuery';
import { LS_CV_SHOW_MAP, countClientSheetFilters, initialShowMap } from './clientViewLayout';
import { formatIngestAge, formatIngestTs } from '../shared/dataFreshness';
import {
  PRIORITY_ORDER,
  TIER_ORDER,
  PRIORITY_LABEL,
  TIER_LABEL,
  CALL_LIST_BAND_ORDER,
  CALL_LIST_BAND_LABEL,
  DEFAULT_SORTS,
  DEFAULT_METRIC_GRAIN,
  type MetricGrain,
  type PriorityKey, type TierKey, type SortEntry, type MetricFilters,
  tierKeyFromIndex,
  pointPriorityCount,
  comparePointsByPriority,
  comparePointsBySorts,
  isDefaultSorts,
  matchesMetricFilters,
  countActiveMetricFilters,
  priorityCountsToObject,
} from './clientViewTokens';
import './ClientViewDashboard.css';
import './InspectionCallList.css';

/** The two passes of a rep's day, in the order they work them. */
type ClientViewMode = 'listing-actions' | 'call-list';

/**
 * Merge a thin map-point (geo + name + tier + priority_counts) with its
 * batch-fetched KPI body into the full `ClientViewDealer` shape the carousel
 * card renders. The body may be absent while the page's bodies are still in
 * flight — the card then renders its header/priority badges from the point and
 * shows zeroed KPIs until the body lands (sub-second).
 */
const mergePointAndCard = (
  point: ClientViewMapPoint,
  body: ClientViewDealerCardBody | undefined,
): ClientViewDealer => ({
  seller_id: point.seller_id,
  seller_name: point.seller_name,
  zip_code: '',
  latitude: point.latitude,
  longitude: point.longitude,
  distance_miles: point.distance_miles,
  territory_city: point.territory_city,
  territory_state: point.territory_state,
  vins_listed_60d: 0,
  vins_sold_60d: 0,
  pfi_vin_count: 0,
  core: body?.core ?? {},
  core_metrics: body?.core_metrics ?? {
    inspections_current: 0,
    inspections_prior: 0,
    mmi_conversion_current: null,
    mmi_conversion_prior: null,
  },
  priority_counts: body?.priority_counts ?? priorityCountsToObject(point.priority_counts),
  last_contact_date: body?.last_contact_date ?? null,
  days_since_last_contact: body?.days_since_last_contact ?? null,
  sfdc_contact_count_30d: body?.sfdc_contact_count_30d ?? 0,
  salesforce_id: body?.salesforce_id ?? null,
  rds_rep: body?.rds_rep ?? null,
});

interface ClientViewDashboardProps {
  userRole?: string;
  impersonatedRole?: string | null;
  /** When false, the Mia assistant is hidden (gated by the rds_mia_agent feature flag). */
  agentEnabled?: boolean;
}

interface FilterMappingRow {
  manager: string;
  territory_market_center: string;
  rds_territory: string;
  seller_uid?: string;
  seller_name: string;
  territory_id?: string;
  parent_territory_id?: string;
  grandparent_territory_id?: string;
}

/** The role-derived default filter selections (manager + RDS territory). */
interface RoleDefaults {
  managers: string[];
  rdsTerritories: string[];
}

/**
 * Derive the default filter selections for a user's role from the filter
 * mapping. Returns empty arrays for admin/default (unfiltered) roles. Shared by
 * the initial load and the Reset button so both land on the same defaults.
 */
const computeRoleDefaults = (
  mapping: FilterMappingRow[],
  effectiveRole: string | null | undefined,
): RoleDefaults => {
  const empty: RoleDefaults = { managers: [], rdsTerritories: [] };
  if (!effectiveRole || effectiveRole === 'admin' || effectiveRole === 'default') return empty;

  const roles = effectiveRole.split(',').map((r) => r.trim());
  const roleLen = roles[0].length;
  const matchingManagers = new Set<string>();
  const matchingTerritories = new Set<string>();

  if (roleLen === 16 || roleLen === 17) {
    for (const role of roles) {
      mapping.forEach((m) => {
        if (m.territory_id === role) {
          if (m.rds_territory) matchingTerritories.add(m.rds_territory);
          if (m.manager) matchingManagers.add(m.manager);
        }
      });
    }
  } else if (roleLen === 10 || roleLen === 13) {
    const idField = roleLen === 10 ? 'grandparent_territory_id' : 'parent_territory_id';
    for (const role of roles) {
      mapping.forEach((m) => {
        const value = (m as any)[idField];
        if (value === role && m.manager) matchingManagers.add(m.manager);
      });
    }
  }

  return {
    managers: Array.from(matchingManagers),
    rdsTerritories: Array.from(matchingTerritories),
  };
};

export const ClientViewDashboard: React.FC<ClientViewDashboardProps> = ({
  userRole = 'default',
  impersonatedRole,
  agentEnabled = true,
}) => {
  // Entry filters
  const [selectedManagers, setSelectedManagers] = useState<string[]>([]);
  const [selectedMarketCenters, setSelectedMarketCenters] = useState<string[]>([]);
  const [selectedRdsTerritories, setSelectedRdsTerritories] = useState<string[]>([]);
  const [selectedCseReps, setSelectedCseReps] = useState<string[]>([]);
  const [selectedSellers, setSelectedSellers] = useState<string[]>([]);

  // Filter option pools
  const [allManagers, setAllManagers] = useState<string[]>([]);
  const [allMarketCenters, setAllMarketCenters] = useState<string[]>([]);
  const [allRdsTerritories, setAllRdsTerritories] = useState<string[]>([]);
  const [allSellers, setAllSellers] = useState<string[]>([]);
  const [filterMapping, setFilterMapping] = useState<FilterMappingRow[]>([]);
  // CSE Rep has its own client-level mapping: the VIN-derived one above has no CSE owner.
  const [cseRepMapping, setCseRepMapping] = useState<ClientViewCseRepOption[]>([]);
  // Role-derived default selections, captured at load so Reset can restore them.
  const roleDefaultsRef = useRef<RoleDefaults>({ managers: [], rdsTerritories: [] });

  // Inspector location
  const [inspectorInput, setInspectorInput] = useState('');
  const [activeInspectorZip, setActiveInspectorZip] = useState<string | null>(null);
  const [activeInspectorSellerId, setActiveInspectorSellerId] = useState<string | null>(null);
  const [radiusMiles, setRadiusMiles] = useState(50);

  // Chip filter state
  const [selectedPriorities, setSelectedPriorities] = useState<PriorityKey[]>([]);
  const [selectedTiers, setSelectedTiers] = useState<TierKey[]>([]);
  // Numeric metric ranges (min/max per card metric) + the carousel sort chain.
  const [metricFilters, setMetricFilters] = useState<MetricFilters>({});
  const [sorts, setSorts] = useState<SortEntry[]>(DEFAULT_SORTS);
  const [metricGrain, setMetricGrain] = useState<MetricGrain>(DEFAULT_METRIC_GRAIN);
  const [selectedVinStatuses, setSelectedVinStatuses] = useState<string[]>([]);
  // RDS-3.5: "Continue in Mia" from a "Where to focus" summary opens the chat on it.
  const [miaHandoff, setMiaHandoff] = useState<AgentHandoff | null>(null);

  // Selection
  const [selectedDealerId, setSelectedDealerId] = useState<string | null>(null);
  const [insightsSellerId, setInsightsSellerId] = useState<string | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<SIFTVehicle | null>(null);
  const [vinPopoutOpen, setVinPopoutOpen] = useState(false);
  const [loadingVehicle, setLoadingVehicle] = useState(false);

  // Data — thin map/list points drive both the map and the carousel list. The
  // KPI-heavy card bodies are fetched per visible page and merged in on render.
  const [points, setPoints] = useState<ClientViewMapPoint[]>([]);
  const [cardBodies, setCardBodies] = useState<Map<string, ClientViewDealerCardBody>>(new Map());
  const [inspectorLocation, setInspectorLocation] = useState<InspectorLocation | null>(null);
  const [vinDataUpdated, setVinDataUpdated] = useState<string | null>(null);
  const [sellerDataUpdated, setSellerDataUpdated] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  // ----- Inspection Call List -----
  const [viewMode, setViewMode] = useState<ClientViewMode>('listing-actions');
  const [callListClients, setCallListClients] = useState<CallListClient[]>([]);
  const [callListBandCounts, setCallListBandCounts] = useState<Partial<Record<CallListBand, number>>>({});
  const [callListSuppressed, setCallListSuppressed] = useState(0);
  const [callListHidden, setCallListHidden] = useState(0);
  const [callListLoading, setCallListLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState<string[]>([]);
  const [statusReasonCodes, setStatusReasonCodes] = useState<string[]>([]);
  const [permanentStatuses, setPermanentStatuses] = useState<string[]>([]);
  const [clientStatuses, setClientStatuses] = useState<Map<string, ClientStatusRecord>>(new Map());
  const [includeSuppressed, setIncludeSuppressed] = useState(false);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [includeDormant, setIncludeDormant] = useState(false);
  const [callListSortBy, setCallListSortBy] = useState<'due' | 'distance'>('due');

  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  // Guards against out-of-order dealer responses: a slow earlier request (e.g. the
  // initial unfiltered load on a cold cache) must not overwrite a fresher filtered
  // result. We bump a sequence id per request and abort the prior in-flight call.
  const fetchSeqRef = useRef(0);
  const fetchAbortRef = useRef<AbortController | null>(null);
  // Tracks whether the initial dealer fetch has fired, so the first load can skip
  // the 300ms filter-debounce and start streaming data immediately.
  const hasFetchedRef = useRef(false);

  // ----- Cascading filter options -----
  const filteredOptions = useMemo(() => {
    const filterMappingsExcluding = (exclude: 'manager' | 'marketCenter' | 'rdsTerritory' | 'seller') => {
      let mappings = filterMapping;
      if (exclude !== 'manager' && selectedManagers.length > 0) {
        mappings = mappings.filter(m => selectedManagers.includes(m.manager));
      }
      if (exclude !== 'marketCenter' && selectedMarketCenters.length > 0) {
        mappings = mappings.filter(m => selectedMarketCenters.includes(m.territory_market_center));
      }
      if (exclude !== 'rdsTerritory' && selectedRdsTerritories.length > 0) {
        mappings = mappings.filter(m => selectedRdsTerritories.includes(m.rds_territory));
      }
      if (exclude !== 'seller' && selectedSellers.length > 0) {
        // Tokens are seller NAMES, seller UIDs, or parent-group names. This mapping is
        // VIN-derived (~28k sellers) while the search is snapshot-backed (~139k
        // clients), and it carries no parent column at all — so a token often matches
        // nothing here. Narrowing to zero rows would empty the Manager / Market Center
        // / RDS Territory dropdowns, so only narrow when the tokens resolved.
        const tokens = new Set(selectedSellers);
        const narrowed = mappings.filter(
          m => tokens.has(m.seller_name) || (m.seller_uid != null && tokens.has(String(m.seller_uid))),
        );
        if (narrowed.length > 0) mappings = narrowed;
      }
      return mappings;
    };
    return {
      managers: [...new Set(filterMappingsExcluding('manager').map(m => m.manager))].filter(Boolean).sort() || allManagers,
      marketCenters: [...new Set(filterMappingsExcluding('marketCenter').map(m => m.territory_market_center))].filter(Boolean).sort() || allMarketCenters,
      rdsTerritories: [...new Set(filterMappingsExcluding('rdsTerritory').map(m => m.rds_territory))].filter(Boolean).sort() || allRdsTerritories,
      sellers: [...new Set(filterMappingsExcluding('seller').map(m => m.seller_name))].filter(Boolean).sort() || allSellers,
      cseReps: [...new Set(
        cseRepMapping
          .filter(m => selectedManagers.length === 0 || (m.manager != null && selectedManagers.includes(m.manager)))
          .filter(m => selectedMarketCenters.length === 0 || (m.territory_market_center != null && selectedMarketCenters.includes(m.territory_market_center)))
          .filter(m => selectedRdsTerritories.length === 0 || (m.rds_territory != null && selectedRdsTerritories.includes(m.rds_territory)))
          .map(m => m.cse_rep),
      )].filter(Boolean).sort(),
    };
  }, [filterMapping, cseRepMapping, selectedManagers, selectedMarketCenters, selectedRdsTerritories, selectedSellers, allManagers, allMarketCenters, allRdsTerritories, allSellers]);

  // ----- Load filter options + apply role-based defaults -----
  useEffect(() => {
    const loadFilters = async () => {
      try {
        const filterOptions = await siftApiClient.getFilterOptions(impersonatedRole || undefined);
        setAllManagers(filterOptions.managers || []);
        setAllMarketCenters(filterOptions.market_centers || []);
        setAllRdsTerritories(filterOptions.rds_territories || []);
        setAllSellers(filterOptions.sellers || []);
        setFilterMapping((filterOptions.mapping || []) as FilterMappingRow[]);
        // Non-blocking: a failure only leaves the CSE Rep dropdown empty.
        siftApiClient.getClientViewCseRepOptions()
          .then(setCseRepMapping)
          .catch((e) => console.error('Failed to load CSE rep options:', e));

        const effectiveRole = impersonatedRole || userRole;
        const mapping = (filterOptions.mapping || []) as FilterMappingRow[];
        const defaults = computeRoleDefaults(mapping, effectiveRole);
        roleDefaultsRef.current = defaults;
        if (defaults.rdsTerritories.length > 0) setSelectedRdsTerritories(defaults.rdsTerritories);
        if (defaults.managers.length > 0) setSelectedManagers(defaults.managers);
        setDefaultsApplied(true);
      } catch (err) {
        console.error('Failed to load filter options:', err);
        setDefaultsApplied(true);
      }
    };
    loadFilters();
  }, [userRole, impersonatedRole]);

  // ----- Fetch dealers -----
  const isInspectorActive = !!(activeInspectorZip || activeInspectorSellerId);
  const fetchDealers = useCallback(async () => {
    // Supersede any in-flight request so its (potentially slower) response can't
    // land after this one and revert the board to a stale/unfiltered set.
    fetchAbortRef.current?.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;
    const seq = ++fetchSeqRef.current;
    try {
      setLoading(true);
      setError(null);
      const result = await siftApiClient.getClientViewMapPoints({
        inspector_zip: activeInspectorZip || undefined,
        inspector_seller_id: activeInspectorSellerId || undefined,
        radius_miles: isInspectorActive ? radiusMiles : undefined,
        manager: selectedManagers.length > 0 ? selectedManagers.join(',') : undefined,
        market_center: selectedMarketCenters.length > 0 ? selectedMarketCenters.join(',') : undefined,
        rds_territory: selectedRdsTerritories.length > 0 ? selectedRdsTerritories.join(',') : undefined,
        cse_rep: selectedCseReps.length > 0 ? selectedCseReps.join(',') : undefined,
        seller_name: selectedSellers.length > 0 ? selectedSellers.join(',') : undefined,
        user_status: selectedVinStatuses.length > 0 ? selectedVinStatuses.join(',') : undefined,
        metric_grain: metricGrain,
        impersonate_role: impersonatedRole || undefined,
      }, controller.signal);
      // Ignore a response that was superseded while in flight.
      if (seq !== fetchSeqRef.current) return;
      setPoints(result.points);
      // KPI bodies are filter-independent but we drop them on a new board to
      // bound memory; the visible page re-fetches its bodies immediately.
      setCardBodies(new Map());
      setInspectorLocation(result.inspector_location);
      setVinDataUpdated(result.vin_data_last_updated ?? null);
      setSellerDataUpdated(result.seller_data_last_updated ?? null);
      setSelectedDealerId(null);
      // Drop any focused-dealer insight too: the prior dealer may not be in the new board, and a
      // lingering id would be a stale focus for both the insights panel and the agent.
      setInsightsSellerId(null);
    } catch (err: any) {
      // A cancelled request was intentionally superseded — leave state untouched.
      if (controller.signal.aborted || err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
      if (seq !== fetchSeqRef.current) return;
      setError(err?.response?.data?.detail || 'Failed to load dealer data');
      setPoints([]);
      setInspectorLocation(null);
    } finally {
      // Only the latest request owns the loading flag.
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [activeInspectorZip, activeInspectorSellerId, isInspectorActive, radiusMiles, selectedManagers, selectedMarketCenters, selectedRdsTerritories, selectedCseReps, selectedSellers, selectedVinStatuses, metricGrain, impersonatedRole]);

  useEffect(() => {
    if (!defaultsApplied) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // First load (no dealers yet) fires immediately so data starts streaming the
    // moment filter defaults resolve; subsequent filter tweaks stay debounced.
    const delay = hasFetchedRef.current ? 300 : 0;
    hasFetchedRef.current = true;
    debounceRef.current = setTimeout(fetchDealers, delay);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [fetchDealers, defaultsApplied]);

  // ----- Fetch call list -----
  // Runs regardless of the active mode: the Listing Actions side shows a live
  // count so the work is visible without the rep having to go looking for it.
  const fetchCallList = useCallback(async () => {
    try {
      setCallListLoading(true);
      const result = await siftApiClient.getInspectionCallList({
        inspector_zip: activeInspectorZip || undefined,
        inspector_seller_id: activeInspectorSellerId || undefined,
        radius_miles: isInspectorActive ? radiusMiles : undefined,
        manager: selectedManagers.length > 0 ? selectedManagers.join(',') : undefined,
        market_center: selectedMarketCenters.length > 0 ? selectedMarketCenters.join(',') : undefined,
        rds_territory: selectedRdsTerritories.length > 0 ? selectedRdsTerritories.join(',') : undefined,
        cse_rep: selectedCseReps.length > 0 ? selectedCseReps.join(',') : undefined,
        seller_name: selectedSellers.length > 0 ? selectedSellers.join(',') : undefined,
        include_suppressed: includeSuppressed || undefined,
        include_hidden: includeHidden || undefined,
        include_dormant: includeDormant || undefined,
        sort_by: callListSortBy,
        impersonate_role: impersonatedRole || undefined,
      });
      setCallListClients(result.clients);
      setCallListBandCounts(result.band_counts || {});
      setCallListSuppressed(result.suppressed_count || 0);
      setCallListHidden(result.hidden_count || 0);
    } catch (err) {
      console.error('Failed to load inspection call list:', err);
      setCallListClients([]);
      setCallListBandCounts({});
    } finally {
      setCallListLoading(false);
    }
  }, [activeInspectorZip, activeInspectorSellerId, isInspectorActive, radiusMiles, selectedManagers, selectedMarketCenters, selectedRdsTerritories, selectedCseReps, selectedSellers, includeSuppressed, includeDormant, includeHidden, callListSortBy, impersonatedRole]);

  useEffect(() => {
    if (!defaultsApplied) return;
    const t = setTimeout(fetchCallList, 300);
    return () => clearTimeout(t);
  }, [fetchCallList, defaultsApplied]);

  useEffect(() => {
    siftApiClient.getClientStatusOptions()
      .then((res) => {
        setStatusOptions(res.options || []);
        setStatusReasonCodes(res.reason_codes || []);
        setPermanentStatuses(res.permanent_statuses || []);
      })
      .catch(() => setStatusOptions([]));
  }, []);


  // Only the two bands that mean "call them now" drive the callout and the tab
  // badge. Both surfaces show the SAME number, and the strip spells out the split,
  // so the count can be reconciled against the sections inside the call list.
  const actionNow = useMemo(() => {
    const dueNow = callListClients.filter((c) => c.band === 'due_now').length;
    const newClients = callListClients.filter((c) => c.band === 'new_client_no_repeat').length;
    return { dueNow, newClients, total: dueNow + newClients };
  }, [callListClients]);

  // Reuse the existing map by projecting call-list rows onto its point shape.
  // Clients with no geocodable ZIP stay in the LIST and are simply not plotted —
  // dropping them would hide the low-volume accounts this feature exists to catch.
  const callListMapPoints = useMemo<ClientViewMapPoint[]>(
    () => callListClients.map((c) => ({
      seller_id: c.seller_id,
      seller_name: c.seller_name,
      latitude: c.latitude,
      longitude: c.longitude,
      distance_miles: c.distance_miles,
      territory_city: c.territory_city,
      territory_state: c.territory_state,
      tier: c.band_rank,
      priority_counts: [],
    })),
    [callListClients],
  );

  // ----- Chip + metric filter dataset -----
  const chipFilteredPoints = useMemo(() => {
    const metricCount = countActiveMetricFilters(metricFilters);
    if (selectedPriorities.length === 0 && selectedTiers.length === 0 && metricCount === 0) return points;
    return points.filter((p) => {
      const tier = tierKeyFromIndex(p.tier);
      const tierMatch = selectedTiers.length === 0 || selectedTiers.includes(tier);
      const priorityMatch =
        selectedPriorities.length === 0 ||
        selectedPriorities.some((k) => pointPriorityCount(p, k) > 0);
      return tierMatch && priorityMatch && (metricCount === 0 || matchesMetricFilters(p, metricFilters));
    });
  }, [points, selectedPriorities, selectedTiers, metricFilters]);

  // Sort: the chained fields picked in the carousel's sort control. The default
  // 'priority' field keeps the original lexicographic order over the priority
  // buckets:
  // - With selected priorities: order respects the user's chip selection sequence.
  // - Without selection: PRIORITY_ORDER (ready_to_list first), ties → seller_name.
  const sortedPoints = useMemo(() => {
    const arr = [...chipFilteredPoints];
    arr.sort((a, b) => comparePointsBySorts(a, b, sorts, selectedPriorities));
    return arr;
  }, [chipFilteredPoints, sorts, selectedPriorities]);

  // ----- Pagination -----
  const PAGE_SIZES = [10, 25, 50, 100];
  const [pageSize, setPageSize] = useState<number>(25);
  const [page, setPage] = useState<number>(1);
  const totalPages = Math.max(1, Math.ceil(sortedPoints.length / pageSize));
  // Reset to page 1 when result set or page size changes
  useEffect(() => { setPage(1); }, [sortedPoints.length, pageSize]);
  const pagedPoints = useMemo(
    () => sortedPoints.slice((page - 1) * pageSize, page * pageSize),
    [sortedPoints, page, pageSize]
  );

  // Paging clears the selection first. The "jump to the selected dealer" effect
  // below re-runs on every page change, so while a dealer was selected it snapped
  // the page straight back and the arrows read as dead.
  const goToPage = useCallback((next: number) => {
    setSelectedDealerId(null);
    setPage(next);
  }, []);

  // Statuses for the cards currently on screen. Fetched per page rather than for
  // the whole board: the unfiltered board is ~140k dealers and the endpoint caps
  // at 500 ids per request.
  const pagedSellerIds = useMemo(() => pagedPoints.map((p) => p.seller_id), [pagedPoints]);
  const fetchCardStatuses = useCallback(async () => {
    if (pagedSellerIds.length === 0) return;
    try {
      const records = await siftApiClient.getClientStatuses(pagedSellerIds);
      setClientStatuses((prev) => {
        const next = new Map(prev);
        for (const r of records) {
          // The endpoint answers for every id asked, using the 'Unassigned'
          // sentinel for one with no status; storing that would render a chip.
          if (r.status && r.status !== 'Unassigned') next.set(r.client_id, r);
          else next.delete(r.client_id);
        }
        return next;
      });
    } catch {
      // Statuses are decoration on the card; a failure must not blank the board.
    }
  }, [pagedSellerIds]);

  useEffect(() => { void fetchCardStatuses(); }, [fetchCardStatuses]);

  const handleSetClientStatus = useCallback(async (
    sellerId: string,
    status: string,
    details: ClientStatusDetails = {},
  ) => {
    try {
      await siftApiClient.setClientStatusBulk([sellerId], status, details);
      await Promise.all([fetchCallList(), fetchCardStatuses()]);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to set client status');
    }
  }, [fetchCallList, fetchCardStatuses]);

  const handleClearClientStatus = useCallback(async (sellerId: string) => {
    try {
      await siftApiClient.clearClientStatus(sellerId);
      setClientStatuses((prev) => {
        const next = new Map(prev);
        next.delete(sellerId);
        return next;
      });
      await fetchCallList();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to clear client status');
    }
  }, [fetchCallList]);

  // Batch-fetch the KPI bodies for the visible page's points (the only rich data
  // ever requested for the carousel). Bodies are cached by seller_id so paging
  // back to a visited page is instant.
  const cardAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    const missing = pagedPoints
      .filter((p) => !cardBodies.has(p.seller_id))
      .map((p) => p.seller_id);
    if (missing.length === 0) return;
    cardAbortRef.current?.abort();
    const controller = new AbortController();
    cardAbortRef.current = controller;
    let cancelled = false;
    (async () => {
      try {
        const res = await siftApiClient.getClientViewDealerCards(missing, controller.signal, metricGrain);
        if (cancelled) return;
        setCardBodies((prev) => {
          const next = new Map(prev);
          for (const c of res.cards) next.set(c.seller_id, c);
          return next;
        });
      } catch {
        // A superseded page fetch is fine; cards render header-only until retried.
        if (controller.signal.aborted) return;
      }
    })();
    return () => { cancelled = true; };
  }, [pagedPoints, cardBodies, metricGrain]);

  // The rich rows the carousel renders: thin point + (possibly pending) KPI body.
  const pagedDealers = useMemo(
    () => pagedPoints.map((p) => mergePointAndCard(p, cardBodies.get(p.seller_id))),
    [pagedPoints, cardBodies]
  );

  // When the selected dealer changes (e.g. user clicks a map marker), make
  // sure the carousel jumps to the page containing that dealer and scrolls
  // its card into view.
  const carouselListRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!selectedDealerId) return;
    const idx = sortedPoints.findIndex((d) => d.seller_id === selectedDealerId);
    if (idx < 0) return;
    const targetPage = Math.floor(idx / pageSize) + 1;
    if (targetPage !== page) {
      setPage(targetPage);
      return; // next render will re-run this effect with the new page
    }
    const localIdx = idx - (page - 1) * pageSize;
    const list = carouselListRef.current;
    if (!list) return;
    const child = list.children[localIdx] as HTMLElement | undefined;
    if (child && typeof child.scrollIntoView === 'function') {
      child.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [selectedDealerId, sortedPoints, pageSize, page]);

  // ----- Carousel ↔ map splitter -----
  const [carouselWidth, setCarouselWidth] = useState<number>(380);
  const splitterRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{ startX: number; startW: number } | null>(null);

  const onSplitterMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragStateRef.current = { startX: e.clientX, startW: carouselWidth };
    const onMove = (ev: MouseEvent) => {
      const s = dragStateRef.current;
      if (!s) return;
      const next = Math.min(720, Math.max(260, s.startW + (ev.clientX - s.startX)));
      setCarouselWidth(next);
    };
    const onUp = () => {
      dragStateRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      // Nudge map to recompute its size after drag ends
      window.dispatchEvent(new Event('resize'));
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [carouselWidth]);

  // ----- Responsive layout (same rules as the Sales View) -----
  const compact = useMediaQuery(COMPACT_QUERY);
  const phone = useMediaQuery(PHONE_QUERY);
  const [showMap, setShowMap] = useState<boolean>(() =>
    initialShowMap(localStorage.getItem(LS_CV_SHOW_MAP), window.matchMedia(COMPACT_QUERY).matches),
  );
  const toggleMap = () => {
    const next = !showMap;
    setShowMap(next);
    // Saved only once chosen, so an untouched device keeps the default for its size.
    localStorage.setItem(LS_CV_SHOW_MAP, next ? '1' : '0');
  };
  const [filtersOpen, setFiltersOpen] = useState(false);
  const sheetOpen = phone && filtersOpen;
  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFiltersOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheetOpen]);

  // ----- Chip counts (count dealers per bucket, not VINs) -----
  const priorityCounts = useMemo(() => {
    const counts = Object.fromEntries(
      PRIORITY_ORDER.map((k) => [k, 0]),
    ) as Record<PriorityKey, number>;
    for (const p of points) {
      for (const k of PRIORITY_ORDER) {
        if (pointPriorityCount(p, k) > 0) counts[k] += 1;
      }
    }
    return counts;
  }, [points]);

  const tierCounts = useMemo(() => {
    const counts: Record<TierKey, number> = {
      core_client: 0, at_risk: 0, emerging: 0, low_mmi_activity: 0, non_mmi_activity: 0, inactive: 0,
    };
    for (const p of points) counts[tierKeyFromIndex(p.tier)] += 1;
    return counts;
  }, [points]);

  const togglePriority = (k: PriorityKey) =>
    setSelectedPriorities((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const toggleTier = (k: TierKey) =>
    setSelectedTiers((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const anyFilterActive =
    selectedPriorities.length > 0 || selectedTiers.length > 0 || isInspectorActive ||
    selectedManagers.length > 0 || selectedMarketCenters.length > 0 ||
    selectedRdsTerritories.length > 0 || selectedCseReps.length > 0 || selectedSellers.length > 0 ||
    countActiveMetricFilters(metricFilters) > 0 || !isDefaultSorts(sorts);

  const handleResetFilters = () => {
    setSelectedPriorities([]);
    setSelectedTiers([]);
    setMetricFilters({});
    setSorts(DEFAULT_SORTS);
    // Restore the user's role-based default filters rather than clearing to nothing.
    setSelectedManagers(roleDefaultsRef.current.managers);
    setSelectedRdsTerritories(roleDefaultsRef.current.rdsTerritories);
    setSelectedCseReps([]);
    setSelectedMarketCenters([]);
    setSelectedSellers([]);
    setInspectorInput('');
    setActiveInspectorZip(null);
    setActiveInspectorSellerId(null);
  };

  // ----- Saved views (personal + shared) -----
  const savedViewSelection = useMemo<ClientViewSelection>(() => ({
    managers: selectedManagers,
    marketCenters: selectedMarketCenters,
    rdsTerritories: selectedRdsTerritories,
    cseReps: selectedCseReps,
    sellers: selectedSellers,
    priorities: selectedPriorities,
    tiers: selectedTiers,
    metricFilters,
    sorts,
    inspector: activeInspectorSellerId || activeInspectorZip || '',
    radiusMiles,
  }), [
    selectedManagers, selectedMarketCenters, selectedRdsTerritories, selectedCseReps, selectedSellers,
    selectedPriorities, selectedTiers, metricFilters, sorts,
    activeInspectorSellerId, activeInspectorZip, radiusMiles,
  ]);

  // The UNFILTERED lists on purpose: filteredOptions is narrowed by the current
  // selection, which would flag perfectly valid saved values as stale.
  const savedViewAvailable = useMemo<ClientViewAvailable>(() => ({
    managers: allManagers,
    marketCenters: allMarketCenters,
    rdsTerritories: allRdsTerritories,
    cseReps: [...new Set(cseRepMapping.map((m) => m.cse_rep))],
  }), [allManagers, allMarketCenters, allRdsTerritories, cseRepMapping]);

  // RDS-1.1: a team set is named after the manager(s) of the districts it is shown to.
  const teamLabel = useCallback((team: { owner: string; audience: string[] }) => {
    const names = new Set<string>();
    for (const m of filterMapping) {
      if (m.parent_territory_id && team.audience.includes(m.parent_territory_id) && m.manager) names.add(m.manager);
    }
    return names.size > 0 ? [...names].sort().join(' / ') : team.owner;
  }, [filterMapping]);

  const handleApplySavedView = useCallback((set: ClientViewSet) => {
    setSelectedManagers(set.managers);    setSelectedMarketCenters(set.marketCenters);
    setSelectedRdsTerritories(set.rdsTerritories);
    setSelectedCseReps(set.cseReps);
    setSelectedSellers(set.sellers);
    setSelectedPriorities(set.priorities);
    setSelectedTiers(set.tiers);
    setMetricFilters(set.metricFilters);
    setSorts(set.sorts);
    setRadiusMiles(set.radiusMiles);
    const inspector = set.inspector.trim();
    setInspectorInput(inspector);
    // Same ZIP-vs-seller-id split the manual Inspector search does.
    setActiveInspectorZip(/^\d{5}$/.test(inspector) ? inspector : null);
    setActiveInspectorSellerId(inspector && !/^\d{5}$/.test(inspector) ? inspector : null);
  }, []);

  // ----- Inspector search -----
  const handleInspectorSearch = () => {
    const clean = inspectorInput.trim();
    if (!clean) {
      setActiveInspectorZip(null);
      setActiveInspectorSellerId(null);
      return;
    }
    if (/^\d{5}$/.test(clean)) {
      setActiveInspectorZip(clean);
      setActiveInspectorSellerId(null);
    } else {
      setActiveInspectorSellerId(clean);
      setActiveInspectorZip(null);
    }
  };
  const handleClearInspector = () => {
    setInspectorInput('');
    setActiveInspectorZip(null);
    setActiveInspectorSellerId(null);
  };

  // ----- VIN drilldown → inline VinDetailsPopout (replaces dealer popout slot) -----
  const handleVinClick = async (row: ClientViewVinRow) => {
    setVinPopoutOpen(true);
    setSelectedVehicle(null);
    setLoadingVehicle(true);
    try {
      const full = await siftApiClient.getVehicleByVin(row.vin);
      setSelectedVehicle(apiVehicleToSIFTVehicle(full));
    } catch (err) {
      console.error('Failed to load vehicle detail', err);
    } finally {
      setLoadingVehicle(false);
    }
  };
  // Board search VIN pick: open the VIN AND scope the board to its client, so the
  // carousel behind the pop-out is the dealer you just drilled into.
  const handleSearchPickVin = useCallback((vin: string, sellerUid?: string | null) => {
    if (sellerUid) setSelectedSellers([String(sellerUid)]);
    setVinPopoutOpen(true);
    setSelectedVehicle(null);
    setLoadingVehicle(true);
    siftApiClient.getVehicleByVin(vin)
      .then((full) => setSelectedVehicle(apiVehicleToSIFTVehicle(full)))
      .catch((err) => console.error('Failed to load vehicle detail', err))
      .finally(() => setLoadingVehicle(false));
  }, []);

  const handleCloseVinPopout = () => {
    setVinPopoutOpen(false);
    setSelectedVehicle(null);
  };

  // ----- Agent UI action directives → drive the dashboard -----
  const handleAgentActions = useCallback((actions: AgentAction[]) => {
    // Before applying a navigation/filter the agent asked for, start from a clean slate: clear any
    // priority/tier chips that were previously checked so the new selection replaces (not stacks on)
    // them. We clear at most once per batch, then let the actions below set the fresh state — this
    // way multiple apply_filter directives in the same batch still accumulate together.
    let filtersCleared = false;
    const clearFiltersOnce = () => {
      if (!filtersCleared) {
        setSelectedPriorities([]);
        setSelectedTiers([]);
        setMetricFilters({});
        filtersCleared = true;
      }
    };

    for (const action of actions) {
      switch (action.type) {
        case 'navigate_dealer':
          if (action.seller_id) {
            // Focusing a new dealer drops any prior dealer/VIN focus and filter chips first.
            clearFiltersOnce();
            setVinPopoutOpen(false);
            setSelectedVehicle(null);
            setSelectedDealerId(action.seller_id);
            setInsightsSellerId(action.seller_id);
          }
          break;
        case 'navigate_vin':
          if (action.vin) {
            // Opening a VIN clears filter chips but keeps the dealer focus it belongs to.
            clearFiltersOnce();
            void handleVinClick({ vin: action.vin } as ClientViewVinRow);
          }
          break;
        case 'apply_filter':
          clearFiltersOnce();
          if (action.priority && (PRIORITY_ORDER as string[]).includes(action.priority)) {
            const p = action.priority as PriorityKey;
            setSelectedPriorities((s) => (s.includes(p) ? s : [...s, p]));
          }
          if (action.tier && (TIER_ORDER as string[]).includes(action.tier)) {
            const t = action.tier as TierKey;
            setSelectedTiers((s) => (s.includes(t) ? s : [...s, t]));
          }
          break;
        case 'set_view':
          // Call-list answers are only actionable if the rep lands on that board mode.
          if (action.view === 'call_list') setViewMode('call-list');
          if (action.view === 'listing_actions') setViewMode('listing-actions');
          break;
        case 'highlight':
          if (action.target) setSelectedDealerId(action.target);
          break;
        case 'back':
          setVinPopoutOpen(false);
          break;
        case 'reset':
          setVinPopoutOpen(false);
          setSelectedVehicle(null);
          setInsightsSellerId(null);
          setSelectedDealerId(null);
          setSelectedPriorities([]);
          setSelectedTiers([]);
          setMetricFilters({});
          setSorts(DEFAULT_SORTS);
          break;
      }
    }
  }, []);


  const agentSelection = useMemo<AgentSelection>(() => {
    const focusId = insightsSellerId ?? selectedDealerId ?? undefined;
    // Only forward a focused dealer that's actually in the current board. insightsSellerId is NOT
    // cleared when the board refetches (filter/scope/inspector change), so a stale id from a prior
    // board could otherwise linger and force every agent dealer lookup to a dealer that no longer
    // exists in scope ("Dealer not found"), trapping the conversation. The agent navigates/clicks
    // only ever target dealers in this set, so a valid focus always survives this guard.
    const validFocus = focusId && points.some((d) => d.seller_id === focusId) ? focusId : undefined;
    return {
      seller_id: validFocus,
      vin: vinPopoutOpen ? selectedVehicle?.vin : undefined,
    };
  }, [insightsSellerId, selectedDealerId, vinPopoutOpen, selectedVehicle, points]);

  // Active board state so the agent reasons over the SAME dealer population the rep sees —
  // territory plus the chip, VIN-status, metric and radius filters they've applied.
  const agentScope = useMemo<AgentScope>(
    () => ({
      managers: selectedManagers.length > 0 ? selectedManagers : undefined,
      market_centers: selectedMarketCenters.length > 0 ? selectedMarketCenters : undefined,
      rds_territories: selectedRdsTerritories.length > 0 ? selectedRdsTerritories : undefined,
      cse_reps: selectedCseReps.length > 0 ? selectedCseReps : undefined,
      metric_grain: metricGrain === 'quarter' ? 'quarter' : undefined,
      seller_names: selectedSellers.length > 0 ? selectedSellers : undefined,
      vin_statuses: selectedVinStatuses.length > 0 ? selectedVinStatuses : undefined,
      priorities: selectedPriorities.length > 0 ? selectedPriorities : undefined,
      tiers: selectedTiers.length > 0 ? selectedTiers : undefined,
      metric_filters: countActiveMetricFilters(metricFilters) > 0 ? metricFilters : undefined,
      inspector_zip: activeInspectorZip || undefined,
      inspector_seller_id: activeInspectorSellerId || undefined,
      radius_miles: isInspectorActive ? radiusMiles : undefined,
    }),
    [
      selectedManagers, selectedMarketCenters, selectedRdsTerritories, selectedCseReps, selectedSellers,
      metricGrain, selectedVinStatuses, selectedPriorities, selectedTiers, metricFilters,
      activeInspectorZip, activeInspectorSellerId, isInspectorActive, radiusMiles,
    ],
  );

  // Client-computed opening snapshot for the agent's welcome card: priority unit counts, tier
  // dealer counts, and the top dealers to start with — all from the same scoped board the user sees.
  const agentBriefing = useMemo<AgentBriefing>(() => {
    const focusedSellerId = insightsSellerId ?? selectedDealerId ?? null;
    const priorities = PRIORITY_ORDER.map((key) => ({
      key,
      label: PRIORITY_LABEL[key],
      units: points.reduce((sum, d) => sum + pointPriorityCount(d, key), 0),
    })).filter((p) => p.units > 0);

    const tierCounts = new Map<TierKey, number>();
    for (const d of points) {
      const t = tierKeyFromIndex(d.tier);
      tierCounts.set(t, (tierCounts.get(t) || 0) + 1);
    }
    const tiers = TIER_ORDER.map((key) => ({
      key,
      label: TIER_LABEL[key],
      dealers: tierCounts.get(key) || 0,
    })).filter((t) => t.dealers > 0);

    // Most urgent band first, matching the call list's own ordering.
    const callListBands = CALL_LIST_BAND_ORDER.map((key) => ({
      key,
      label: CALL_LIST_BAND_LABEL[key],
      clients: callListBandCounts[key] || 0,
    })).filter((b) => b.clients > 0);

    const topDealers = [...points]
      .sort((a, b) => comparePointsByPriority(a, b, []))
      .map((d) => {
        const topKey = PRIORITY_ORDER.find((k) => pointPriorityCount(d, k) > 0);
        return {
          sellerId: d.seller_id,
          name: d.seller_name || `Dealer ${d.seller_id}`,
          bucketLabel: topKey ? PRIORITY_LABEL[topKey].toLowerCase() : 'units',
          count: topKey ? pointPriorityCount(d, topKey) : 0,
        };
      })
      .filter((d) => d.count > 0)
      .slice(0, 3);

    return {
      ready: defaultsApplied && !loading,
      dealerCount: points.length,
      priorities,
      tiers,
      callListBands,
      topDealers,
      selectedDealer: focusedSellerId
        ? {
            sellerId: focusedSellerId,
            name:
              points.find((d) => d.seller_id === focusedSellerId)?.seller_name ||
              `Dealer ${focusedSellerId}`,
          }
        : undefined,
      selectedVehicle:
        vinPopoutOpen && selectedVehicle
          ? {
              vin: selectedVehicle.vin,
              label:
                [selectedVehicle.model_year, selectedVehicle.make, selectedVehicle.model]
                  .filter(Boolean)
                  .join(' ') || `VIN ${selectedVehicle.vin}`,
            }
          : undefined,
    };
  }, [points, defaultsApplied, loading, insightsSellerId, selectedDealerId, vinPopoutOpen, selectedVehicle, callListBandCounts]);


  // Shared between the desktop rows and the phone's Filters sheet.
  const entryDropdowns = (
    <>
      <MultiSelectDropdown label="Manager" options={filteredOptions.managers} values={selectedManagers} onChange={setSelectedManagers} placeholder="All Managers" />
      <MultiSelectDropdown label="Market Center" options={filteredOptions.marketCenters} values={selectedMarketCenters} onChange={setSelectedMarketCenters} placeholder="All Market Centers" />
      <MultiSelectDropdown label="RDS Territory" options={filteredOptions.rdsTerritories} values={selectedRdsTerritories} onChange={setSelectedRdsTerritories} placeholder="All Territories" />
      <MultiSelectDropdown label="CSE Rep" options={filteredOptions.cseReps} values={selectedCseReps} onChange={setSelectedCseReps} placeholder="All CSE Reps" />
    </>
  );
  const modeSwitch = (
    <div className="cv-mode-switch" role="tablist" aria-label="Client View mode">
      <button
        role="tab"
        aria-selected={viewMode === 'listing-actions'}
        className={`cv-mode-btn${viewMode === 'listing-actions' ? ' cv-mode-btn-active' : ''}`}
        onClick={() => setViewMode('listing-actions')}
      >
        Listing Actions
      </button>
      <button
        role="tab"
        aria-selected={viewMode === 'call-list'}
        className={`cv-mode-btn${viewMode === 'call-list' ? ' cv-mode-btn-active' : ''}`}
        onClick={() => setViewMode('call-list')}
      >
        Inspection Call List
        {actionNow.total > 0 && (
          <span
            className="cv-mode-badge"
            title="Clients due now plus new clients with no repeat inspection — the two bands that need a call today."
          >
            {actionNow.total.toLocaleString()}
          </span>
        )}
      </button>
    </div>
  );
  const mapToggle = (
    <button
      type="button"
      className={`cv-map-toggle${showMap ? ' active' : ''}`}
      aria-pressed={showMap}
      onClick={toggleMap}
      title={showMap ? 'Hide the map and give the dealers the full page' : 'Show the map'}
    >
      Map
    </button>
  );
  const chipRows = (
    <div className="cv-chip-rows">
      <TierChipRow
        // RDS-1.2: tiers read the quarter on the Quarter grain, the rolling period otherwise.
        label={metricGrain === 'quarter' ? 'Tier (quarter)' : 'Tier (period)'}
        counts={tierCounts}
        selected={selectedTiers}
        onToggle={toggleTier}
        disabled={viewMode === 'call-list'}
        disabledHint="Tier filters apply to Listing Actions. The Inspection Call List is grouped by call reason instead."
      />
      <PriorityChipRow
        counts={priorityCounts}
        selected={selectedPriorities}
        onToggle={togglePriority}
        disabled={viewMode === 'call-list'}
        disabledHint="Priority filters apply to Listing Actions. The Inspection Call List is grouped by call reason instead."
      />
    </div>
  );
  const inspectorGroup = (
    <div className="cv-inspector-group">
      <div className="cv-inspector-line">
        <label className="cv-search-label">Inspector Location</label>
        <input
          type="text"
          className="cv-zip-input"
          placeholder="ZIP or Seller #"
          value={inspectorInput}
          onChange={(e) => setInspectorInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleInspectorSearch(); }}
          maxLength={20}
        />
        <button className="cv-search-btn" onClick={handleInspectorSearch} disabled={loading}>Search</button>
        {isInspectorActive && <button className="cv-clear-btn" onClick={handleClearInspector}>Clear</button>}
      </div>
      {isInspectorActive && (
        <div className="cv-inspector-line">
          <label className="cv-search-label">Radius: {radiusMiles} mi</label>
          <input type="range" className="cv-radius-slider" min={10} max={200} step={5} value={radiusMiles} onChange={(e) => setRadiusMiles(Number(e.target.value))} />
        </div>
      )}
      <div className="cv-inspector-line cv-inspector-meta">
        {inspectorLocation && (
          <span className="cv-location-display">
            {inspectorLocation.seller_name
              ? `${inspectorLocation.seller_name} — ${inspectorLocation.city}${inspectorLocation.city && inspectorLocation.state ? ', ' : ''}${inspectorLocation.state}`
              : `${inspectorLocation.city}${inspectorLocation.city && inspectorLocation.state ? ', ' : ''}${inspectorLocation.state}`}
          </span>
        )}
        <span className="cv-stat">
          {(loading || !defaultsApplied) ? '...' : sortedPoints.length} of {points.length} dealer{points.length !== 1 ? 's' : ''}
        </span>
        <ClientSavedViews
          current={savedViewSelection}
          onApply={handleApplySavedView}
          available={savedViewAvailable}
          teamLabel={teamLabel}
        />
        {anyFilterActive && (
          <button className="cv-reset-btn" onClick={handleResetFilters}>Reset</button>
        )}
      </div>
    </div>
  );
  const sheetFilterCount = countClientSheetFilters({
    managers: selectedManagers,
    marketCenters: selectedMarketCenters,
    rdsTerritories: selectedRdsTerritories,
    cseReps: selectedCseReps,
    tiers: selectedTiers,
    priorities: selectedPriorities,
    inspectorActive: isInspectorActive,
  });
  const popout = vinPopoutOpen ? (
    <VinDetailsPopout
      vehicle={selectedVehicle}
      isLoading={loadingVehicle}
      onClose={handleCloseVinPopout}
      onBack={insightsSellerId ? () => setVinPopoutOpen(false) : undefined}
      backLabel="Back to Dealer"
      fullScreen={compact}
      miaEnabled={agentEnabled}
      onContinueInMia={agentEnabled ? setMiaHandoff : undefined}
    />
  ) : insightsSellerId ? (
    <DealerInsightsPopout
      sellerId={insightsSellerId}
      onClose={() => setInsightsSellerId(null)}
      onVinClick={handleVinClick}
      fullScreen={compact}
      miaEnabled={agentEnabled}
      onContinueInMia={agentEnabled ? setMiaHandoff : undefined}
    />
  ) : null;
  const map = (
    <div className="cv-map-container">
      <ClientViewMap
        points={viewMode === 'call-list' ? callListMapPoints : sortedPoints}
        inspectorLocation={inspectorLocation}
        radiusMiles={isInspectorActive ? radiusMiles : null}
        isInspectorMode={isInspectorActive}
        loading={viewMode === 'call-list' ? callListLoading : loading}
        selectedDealerId={selectedDealerId}
        onDealerClick={(id) => setSelectedDealerId(id)}
      />
    </div>
  );
  // Desktop keeps the side-by-side layout; a popout takes the map's slot, or opens one if the
  // map is off. Tablets and phones stack the map over the list and open popouts full screen.
  const sidePane = compact ? null : (popout ?? (showMap ? map : null));

  return (
    <div className={`cv-dashboard${compact ? ' cv-dashboard--compact' : ''}${phone ? ' cv-dashboard--phone' : ''}`}>
      {phone ? (
        <div className="cv-mobile-bar">
          <ClientBoardSearch
            selected={selectedSellers}
            onChange={setSelectedSellers}
            onPickVin={handleSearchPickVin}
          />
          <button
            type="button"
            className={`cv-filters-btn${sheetFilterCount > 0 ? ' active' : ''}`}
            aria-expanded={sheetOpen}
            onClick={() => setFiltersOpen(true)}
          >
            Filters
            {sheetFilterCount > 0 && <span className="cv-filters-badge">{sheetFilterCount}</span>}
          </button>
        </div>
      ) : (
      <div className="cv-filters-row">
        <div className="cv-filter-group">
          {entryDropdowns}
          <ClientBoardSearch
            selected={selectedSellers}
            onChange={setSelectedSellers}
            onPickVin={handleSearchPickVin}
          />
          <div className="cv-mode-field">
            <label>View</label>
            <div className="cv-mode-row">
              {modeSwitch}
              {mapToggle}
            </div>
          </div>
        </div>
        <div className="cv-data-freshness">
          <span
            className="cv-data-freshness-line"
            title={`VIN data last updated ${formatIngestTs(vinDataUpdated)}`}
          >
            VIN <strong>{formatIngestAge(vinDataUpdated)}</strong>
          </span>
          <span
            className="cv-data-freshness-line"
            title={`Client data last updated ${formatIngestTs(sellerDataUpdated)}`}
          >
            Client <strong>{formatIngestAge(sellerDataUpdated)}</strong>
          </span>
        </div>
      </div>
      )}

      {phone ? (
        <div className="cv-phone-row">
          {modeSwitch}
          {mapToggle}
        </div>
      ) : (
        <div className="cv-control-row">
          {chipRows}
          {inspectorGroup}
        </div>
      )}

      {error && <div className="cv-error">{error}</div>}

      {viewMode === 'listing-actions' && actionNow.total > 0 && (
        <div
          className="cv-callout-strip"
          role="button"
          tabIndex={0}
          onClick={() => setViewMode('call-list')}
          onKeyDown={(e) => { if (e.key === 'Enter') setViewMode('call-list'); }}
        >
          <span className="cv-callout-count">{actionNow.total}</span>
          <span>
            client{actionNow.total === 1 ? '' : 's'} need a scheduling call
            {' — '}
            <strong>{actionNow.dueNow}</strong> due now,{' '}
            <strong>{actionNow.newClients}</strong> new with no repeat inspection
          </span>
          <span className="cv-callout-link">Open the Inspection Call List →</span>
        </div>
      )}

      {/* Content: carousel + (map or popout) with drag-resize splitter */}
      <div className={`cv-content${compact ? ' cv-content--stacked' : ''}`}>
        {compact && showMap && map}
        <div
          className={`cv-carousel${compact || !sidePane ? ' cv-carousel--full' : ''}`}
          style={compact || !sidePane ? undefined : { width: carouselWidth }}
        >
          {viewMode === 'call-list' ? (
            <InspectionCallList
              clients={callListClients}
              loading={callListLoading}
              bandCounts={callListBandCounts}
              suppressedCount={callListSuppressed}
              hiddenCount={callListHidden}
              statusOptions={statusOptions}
              reasonCodes={statusReasonCodes}
              permanentStatuses={permanentStatuses}
              selectedClientId={selectedDealerId}
              includeSuppressed={includeSuppressed}
              includeHidden={includeHidden}
              includeDormant={includeDormant}
              sortBy={callListSortBy}
              isLocationMode={isInspectorActive}
              onToggleSuppressed={setIncludeSuppressed}
              onToggleHidden={setIncludeHidden}
              onToggleDormant={setIncludeDormant}
              onSortByChange={setCallListSortBy}
              onClientClick={(id) => { setSelectedDealerId(id); setInsightsSellerId(id); }}
              onSetStatus={handleSetClientStatus}
              onClearStatus={handleClearClientStatus}
            />
          ) : (
          <>
          <div className="cv-carousel-header">
            <MetricGrainControl value={metricGrain} onChange={setMetricGrain} />
            <CarouselSortControl sorts={sorts} onChange={setSorts} />
            <MetricFiltersPopover filters={metricFilters} onChange={setMetricFilters} />
            <VinStatusFilterPopover selected={selectedVinStatuses} onChange={setSelectedVinStatuses} />
          </div>
          <div className="cv-carousel-list" ref={carouselListRef}>
            {(loading || !defaultsApplied) && pagedDealers.length === 0 ? (
              Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="cv-card-skeleton" aria-hidden="true">
                  <div className="cv-skeleton cv-skeleton-line" />
                  <div className="cv-skeleton cv-skeleton-line-sm" />
                  <div className="cv-skeleton cv-skeleton-block" />
                  <div className="cv-skeleton-pills">
                    <div className="cv-skeleton cv-skeleton-pill" />
                    <div className="cv-skeleton cv-skeleton-pill" />
                    <div className="cv-skeleton cv-skeleton-pill" />
                  </div>
                </div>
              ))
            ) : pagedDealers.length === 0 ? (
              <div className="cv-empty">
                No dealers match the current filters.
              </div>
            ) : (
              pagedDealers.map((dealer) => (
                <ClientViewDealerCard
                  key={dealer.seller_id}
                  dealer={dealer}
                  grain={metricGrain}
                  selected={dealer.seller_id === selectedDealerId}
                  status={clientStatuses.get(dealer.seller_id) ?? null}
                  statusOptions={statusOptions}
                  reasonCodes={statusReasonCodes}
                  permanentStatuses={permanentStatuses}
                  onSetStatus={handleSetClientStatus}
                  onClearStatus={handleClearClientStatus}
                  onClick={() => {
                    setSelectedDealerId(dealer.seller_id);
                    setInsightsSellerId(dealer.seller_id);
                  }}
                />
              ))
            )}
          </div>
          {sortedPoints.length > 0 && (
            <div className="cv-pagination">
              <button
                className="cv-page-btn"
                onClick={() => goToPage(1)}
                disabled={page === 1}
                aria-label="First page"
              >«</button>
              <button
                className="cv-page-btn"
                onClick={() => goToPage(Math.max(1, page - 1))}
                disabled={page === 1}
                aria-label="Previous page"
              >‹</button>
              <span className="cv-page-indicator">
                <span className="cv-page-of">{page} / {totalPages}</span>
                <span className="cv-page-sep" aria-hidden="true">·</span>
                <span className="cv-page-range">
                  {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, sortedPoints.length)} of {sortedPoints.length.toLocaleString()}
                </span>
              </span>
              <button
                className="cv-page-btn"
                onClick={() => goToPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                aria-label="Next page"
              >›</button>
              <button
                className="cv-page-btn"
                onClick={() => goToPage(totalPages)}
                disabled={page >= totalPages}
                aria-label="Last page"
              >»</button>
              <select
                className="cv-page-size"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                aria-label="Cards per page"
              >
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}/page</option>)}
              </select>
            </div>
          )}
          </>
          )}
        </div>

        {sidePane && (
          <div
            ref={splitterRef}
            className="cv-splitter"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize carousel"
            onMouseDown={onSplitterMouseDown}
          />
        )}
        {sidePane}
      </div>

      {compact && popout && <div className="cv-drawer-overlay">{popout}</div>}

      {sheetOpen && (
        <div className="cv-filter-sheet" role="dialog" aria-modal="true" aria-label="Filters">
          <div className="cv-filter-sheet-head">
            <span className="cv-filter-sheet-title">Filters</span>
            <button type="button" className="cv-filter-sheet-done" onClick={() => setFiltersOpen(false)}>
              Done
            </button>
          </div>
          <div className="cv-filter-sheet-body">
            <div className="cv-filter-sheet-dropdowns">{entryDropdowns}</div>
            {chipRows}
            {inspectorGroup}
          </div>
          <div className="cv-filter-sheet-foot">
            {anyFilterActive && (
              <button type="button" className="cv-reset-btn" onClick={handleResetFilters}>Reset</button>
            )}
            <button type="button" className="cv-filter-sheet-apply" onClick={() => setFiltersOpen(false)}>
              Show {sortedPoints.length.toLocaleString()} dealer{sortedPoints.length === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      )}

      {agentEnabled && (
        <AgentChatWidget selection={agentSelection} scope={agentScope} scopeReady={defaultsApplied} briefing={agentBriefing} onActions={handleAgentActions} handoff={miaHandoff} />
      )}
    </div>
  );
};
